import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useEffect } from 'react'
import { getAuthBridge } from '@/auth/auth-bridge'
import { useAuth } from '@/hooks/useAuth'
import { registerFileJobMonitor, resetFileJobMonitor } from './file-job-monitor'
import { registerNotificationBridge } from './notification-bridge'
import { invalidateOnReconnect, registerQueryBridge } from './query-bridge'
import { realtimeConfig } from './realtime-config'
import { startRealtimeConnection } from './realtime-connection'

// Owns the single realtime connection for the session (ADR-0020). Renders its
// children unchanged — it is a lifecycle host, not a UI boundary, and consumers
// reach the channel through `subscribeRealtime` rather than through this tree.
//
// Nothing here throws or blocks: with realtime unconfigured, or with no signed-in
// user, the app simply keeps its polling behaviour.

export interface RealtimeProviderProps {
	children: ReactNode
}

export function RealtimeProvider({ children }: RealtimeProviderProps) {
	const { isAuthenticated, user } = useAuth()
	const queryClient = useQueryClient()
	const userId = user?.userId

	useEffect(() => registerQueryBridge(queryClient), [queryClient])

	// Registered here, not in a page: a processing failure has to reach the user
	// wherever they navigated to while the work ran.
	useEffect(() => registerNotificationBridge(), [])

	// Same reason, for background ZIP generation: the page that started it is not
	// the page the user is on minutes later when it finishes.
	useEffect(() => registerFileJobMonitor(queryClient), [queryClient])

	useEffect(() => {
		// `realtimeConfig` is null when the deployment has no channel; treat that
		// exactly like being signed out — no connection, no error, polling continues.
		if (!isAuthenticated || !userId || realtimeConfig === null) return

		const connection = startRealtimeConnection({
			config: realtimeConfig,
			userId,
			// Resolved per attempt, never captured: a connection outlives its token,
			// so a reconnect an hour in must not reuse the one from mount.
			getToken: async () => {
				const bridge = getAuthBridge()
				if (!bridge) return null
				return bridge.getAccessToken() ?? (await bridge.renew())
			},
			onResubscribed: () => {
				invalidateOnReconnect(queryClient)
			},
		})

		// Also covers logout: signing out flips `isAuthenticated`, which runs this
		// cleanup and drops the subscription rather than leaving one authorized
		// against the authorizer's 300s result cache.
		// This cleanup is the one place in the app that means "the authenticated
		// session ended" rather than "a component unmounted", so it is also where
		// the File Job monitor drops what it was following on that session's behalf
		// — otherwise its timers would keep polling across a logout in the same tab.
		return () => {
			connection.stop()
			resetFileJobMonitor()
		}
	}, [isAuthenticated, userId, queryClient])

	return children
}
